import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'bootstrap/js/dist/modal';

export interface IVideo {
	src: string;
}

export interface IVideoProps extends IBaseElementProps {
	url?: IVideo;
	/**
	 * You can paste embed code from video streaming sites like YouTube or Vimeo here.
	 * @control textarea
	 */
	oembed?: string;
	poster?: IImage;
	playLabel?: string;
	autoPlay?: boolean;
}

export const getYoutubeIDFromURL = (url: string): string | boolean => {
	var regExp =
		/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
	var match = url.match(regExp);
	return match && match[7].length == 11 ? match[7] : false;
};

/**
 * Video
 * @block
 * @requiresHooks
 */
const Video: React.FC<IVideoProps> = ({
	className,
	classNames = [],
	url,
	oembed,
	poster,
	playLabel = 'Play video',
	autoPlay = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(autoPlay);
	const [iframeLoaded, setIframeLoaded] = useState(false);
	const ref = useRef(null);
	let embedURL = '';
	let isYoutubeVideo = false;
	let thumbURL = poster ? poster.src : '';

	const handleClick = (event: any) => {
		const target = event.target as HTMLButtonElement;

		if (isYoutubeVideo) {
			setIsPlaying(true);
		} else {
			ref.current && (ref.current! as HTMLVideoElement).play();
		}

		if (!iframeLoaded && isYoutubeVideo) {
			const currentVideoLoadingContent = target.parentElement
				.querySelector('.video__embed-loading')
				.classList.remove('d-none');
			const currentVideoEmbedContent =
				target.previousSibling.parentElement.querySelector(
					'.video__embed-content',
				);
			currentVideoEmbedContent.innerHTML = oembed;
			const videoEmbedIframe =
				currentVideoEmbedContent.querySelector('iframe');
			videoEmbedIframe.addEventListener('load', () => {
				setIframeLoaded(true);
				currentVideoEmbedContent.classList.remove('d-none');
			});
		}
	};

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		if (!!url) {
			const video = ref.current! as HTMLVideoElement;
			video.addEventListener('play', () => {
				setIsPlaying(true);
			});

			video.addEventListener('pause', () => {
				setIsPlaying(false);
			});
		} else if (!!oembed && ref.current) {
			document.body.appendChild(ref.current);
		}
	}, []);

	if (!!oembed) {
		const urlMatches = oembed!.match(/\bhttps?:\/\/\S+/gi);
		if (urlMatches && urlMatches.length > 0) {
			embedURL = urlMatches[0];

			if (
				embedURL.indexOf('youtube.com/') > -1 ||
				embedURL.indexOf('youtu.be/') > -1
			) {
				isYoutubeVideo = true;

				if (!thumbURL) {
					thumbURL = `https://i1.ytimg.com/vi/${getYoutubeIDFromURL(
						embedURL,
					)}/hqdefault.jpg`;
				}
			}
		}
	}

	return (
		<div
			className={
				className ||
				cn(
					'video',
					isPlaying && 'video--playing',
					thumbURL && 'video--poster',
					...classNames,
				)
			}
			style={
				!isPlaying && thumbURL
					? { backgroundImage: `url("${thumbURL}")` }
					: {}
			}
		>
			{!!url && url.src && (
				<video
					ref={ref}
					controls
					poster={poster?.src}
					autoPlay={autoPlay}
				>
					<source src={url.src} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}

			{!!oembed && (
				<>
					<>
						{!iframeLoaded && isYoutubeVideo && (
							<div className="video__embed-loading d-none">
								<div
									className="spinner-border text-primary"
									role="status"
								>
									<span className="visually-hidden">
										Loading...
									</span>
								</div>
							</div>
						)}
					</>
					<div className="video__embed">
						<div className="video__embed-content d-none"></div>
					</div>
				</>
			)}

			{!isPlaying && (
				<button
					className="btn-play"
					onClick={(e) => {
						handleClick(e);
					}}
					aria-label="Play video"
				>
					{playLabel}
				</button>
			)}
		</div>
	);
};

export default Video;
